import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Navigation, Award, Upload, FileText, Trash2, Download, Clock } from 'lucide-react';
import { toast } from 'sonner';

var API_URL = process.env.REACT_APP_BACKEND_URL;

// Fleet Tracking Tab Component
export function FleetTrackingTab(props) {
  var fleetDashboard = props.fleetDashboard;
  var onAssignBadge = props.onAssignBadge;
  var onMonitor = props.onMonitor;

  return (
    <div>
      <FleetStatsCards fleetDashboard={fleetDashboard} />
      <FleetManagersList fleetDashboard={fleetDashboard} onAssignBadge={onAssignBadge} onMonitor={onMonitor} />
    </div>
  );
}

// Fleet Stats Cards
export function FleetStatsCards(props) {
  var fleetDashboard = props.fleetDashboard;
  var totalActive = fleetDashboard ? fleetDashboard.total_active : 0;
  var totalKmToday = fleetDashboard ? fleetDashboard.total_km_today : 0;
  var totalFleet = fleetDashboard && fleetDashboard.fleet_managers ? fleetDashboard.fleet_managers.length : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6">
          <p className="text-gray-400 text-sm">Active Fleet Managers</p>
          <p className="text-3xl font-bold text-green-400">{totalActive}</p>
        </CardContent>
      </Card>
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6">
          <p className="text-gray-400 text-sm">Total KM Today</p>
          <p className="text-3xl font-bold text-blue-400">{totalKmToday} km</p>
        </CardContent>
      </Card>
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6">
          <p className="text-gray-400 text-sm">Total Fleet</p>
          <p className="text-3xl font-bold text-white">{totalFleet}</p>
        </CardContent>
      </Card>
    </div>
  );
}

// Fleet Managers List
export function FleetManagersList(props) {
  var fleetDashboard = props.fleetDashboard;
  var onAssignBadge = props.onAssignBadge;
  var onMonitor = props.onMonitor;
  var managers = fleetDashboard && fleetDashboard.fleet_managers ? fleetDashboard.fleet_managers : [];

  var managerItems = [];
  for (var i = 0; i < managers.length; i++) {
    var fm = managers[i];
    managerItems.push(
      <FleetManagerRow key={fm.id} fm={fm} onAssignBadge={onAssignBadge} onMonitor={onMonitor} />
    );
  }

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Navigation className="w-5 h-5 text-blue-400" />
          Fleet Manager Live Tracking
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {managerItems}
          {managers.length === 0 && (
            <p className="text-center text-gray-400 py-8">No fleet managers found</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Individual Fleet Manager Row
export function FleetManagerRow(props) {
  var fm = props.fm;
  var onAssignBadge = props.onAssignBadge;
  var onMonitor = props.onMonitor;

  var statusClass = fm.is_active ? 'bg-green-500 animate-pulse' : 'bg-gray-500';
  var badgeClass = '';
  var badgeEmoji = '';
  
  if (fm.weekly_badge === 'gold') {
    badgeClass = 'bg-yellow-500/20 text-yellow-300';
    badgeEmoji = '🥇';
  } else if (fm.weekly_badge === 'silver') {
    badgeClass = 'bg-slate-400/20 text-slate-300';
    badgeEmoji = '🥈';
  } else if (fm.weekly_badge === 'bronze') {
    badgeClass = 'bg-amber-700/20 text-amber-400';
    badgeEmoji = '🥉';
  }

  var locationDisplay = null;
  if (fm.location && fm.is_active) {
    var lat = fm.location.latitude;
    var lng = fm.location.longitude;
    locationDisplay = (
      <div className="text-center">
        <p className="text-xs text-gray-400">Location</p>
        <p className="text-xs text-green-400">{lat.toFixed(4)}, {lng.toFixed(4)}</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg" data-testid={'fleet-manager-' + fm.id}>
      <div className="flex items-center gap-4">
        <div className={'w-3 h-3 rounded-full ' + statusClass}></div>
        <div 
          className="cursor-pointer hover:bg-slate-600/50 p-1 rounded transition-colors"
          onClick={function() { if (onMonitor) onMonitor(fm.id); }}
          title="Click to view detailed monitoring"
        >
          <p className="text-white font-medium hover:text-blue-400">{fm.name}</p>
          <p className="text-sm text-gray-400">{fm.phone}</p>
        </div>
        {fm.weekly_badge && (
          <Badge className={badgeClass}>
            {badgeEmoji} {fm.weekly_badge}
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-6">
        <div className="text-center">
          <p className="text-lg font-bold text-blue-400">{fm.today_km} km</p>
          <p className="text-xs text-gray-400">Today</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-amber-400">{fm.pending_tasks}</p>
          <p className="text-xs text-gray-400">Tasks</p>
        </div>
        {locationDisplay}
        <Button 
          size="sm" 
          onClick={function() { if (onMonitor) onMonitor(fm.id); }} 
          className="bg-green-600 hover:bg-green-700"
          title="Monitor Employee"
        >
          <Award className="w-4 h-4 mr-1" />Monitor
        </Button>
        <Button 
          size="sm" 
          onClick={function() { onAssignBadge(fm); }} 
          className="bg-purple-600 hover:bg-purple-700"
        >
          <Award className="w-4 h-4 mr-1" />Badge
        </Button>
      </div>
    </div>
  );
}

// Badge Assignment Modal
export function BadgeAssignmentModal(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var selectedFleetManager = props.selectedFleetManager;
  var onAssignBadge = props.onAssignBadge;
  var onRemoveBadge = props.onRemoveBadge;

  if (!selectedFleetManager) return null;

  function handleBadgeClick(badgeType) {
    onAssignBadge(badgeType);
  }

  function handleRemove() {
    onRemoveBadge(selectedFleetManager.id);
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-400" />
            Assign Weekly Badge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-slate-700/30 rounded-lg">
            <p className="text-white font-medium">{selectedFleetManager.name}</p>
            <p className="text-sm text-gray-400">{selectedFleetManager.phone}</p>
            <p className="text-sm text-blue-400 mt-1">Today: {selectedFleetManager.today_km} km driven</p>
          </div>
          <p className="text-sm text-gray-400">Select a badge to award for this week's task completion:</p>
          <div className="grid grid-cols-3 gap-3">
            <Button 
              onClick={function() { handleBadgeClick('bronze'); }} 
              className="flex flex-col items-center p-4 h-auto bg-gradient-to-br from-amber-700/50 to-amber-900/50 hover:from-amber-700 hover:to-amber-900 border border-amber-500/30"
            >
              <span className="text-3xl mb-2">🥉</span>
              <span className="text-white font-medium">Bronze</span>
              <span className="text-xs text-gray-300">Good Performance</span>
            </Button>
            <Button 
              onClick={function() { handleBadgeClick('silver'); }} 
              className="flex flex-col items-center p-4 h-auto bg-gradient-to-br from-slate-400/50 to-slate-600/50 hover:from-slate-400 hover:to-slate-600 border border-slate-400/30"
            >
              <span className="text-3xl mb-2">🥈</span>
              <span className="text-white font-medium">Silver</span>
              <span className="text-xs text-gray-300">Great Performance</span>
            </Button>
            <Button 
              onClick={function() { handleBadgeClick('gold'); }} 
              className="flex flex-col items-center p-4 h-auto bg-gradient-to-br from-yellow-500/50 to-yellow-700/50 hover:from-yellow-500 hover:to-yellow-700 border border-yellow-500/30"
            >
              <span className="text-3xl mb-2">🥇</span>
              <span className="text-white font-medium">Gold</span>
              <span className="text-xs text-gray-300">Excellent Performance</span>
            </Button>
          </div>
          {selectedFleetManager.weekly_badge && (
            <Button 
              onClick={handleRemove} 
              variant="outline" 
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
            >
              Remove Current Badge
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SALARY SLIPS ADMIN ====================

export function SalarySlipsAdminTab(props) {
  var employees = props.employees || [];
  var [salarySlips, setSalarySlips] = useState([]);
  var [showUploadModal, setShowUploadModal] = useState(false);
  var [selectedEmployee, setSelectedEmployee] = useState('');
  var [selectedMonth, setSelectedMonth] = useState('');
  var [amount, setAmount] = useState('');
  var [notes, setNotes] = useState('');
  var [file, setFile] = useState(null);
  var [uploading, setUploading] = useState(false);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    fetchSalarySlips();
  }, []);

  function fetchSalarySlips() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/salary-slips')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setSalarySlips(d || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  function handleUpload() {
    if (!selectedEmployee || !selectedMonth || !amount || !file) {
      toast.error('Please fill all required fields');
      return;
    }

    setUploading(true);
    var formData = new FormData();
    formData.append('month', selectedMonth);
    formData.append('amount', amount);
    formData.append('notes', notes);
    formData.append('file', file);

    fetch(API_URL + '/api/employee-admin/salary-slip/' + selectedEmployee, {
      method: 'POST',
      body: formData
    })
      .then(function(r) {
        if (!r.ok) return r.json().then(function(d) { throw new Error(d.detail); });
        return r.json();
      })
      .then(function() {
        toast.success('Salary slip uploaded!');
        setShowUploadModal(false);
        resetForm();
        fetchSalarySlips();
      })
      .catch(function(e) { toast.error(e.message); })
      .finally(function() { setUploading(false); });
  }

  function handleDelete(slipId) {
    if (!window.confirm('Delete this salary slip?')) return;
    fetch(API_URL + '/api/employee-admin/salary-slip/' + slipId, { method: 'DELETE' })
      .then(function() { toast.success('Deleted'); fetchSalarySlips(); });
  }

  function resetForm() {
    setSelectedEmployee('');
    setSelectedMonth('');
    setAmount('');
    setNotes('');
    setFile(null);
  }

  // Generate month options for last 12 months
  var monthOptions = [];
  var now = new Date();
  for (var i = 0; i < 12; i++) {
    var d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    var value = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    var label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    monthOptions.push({ value: value, label: label });
  }

  var employeeOptions = [];
  for (var j = 0; j < employees.length; j++) {
    var emp = employees[j];
    employeeOptions.push(
      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_type.replace('_', ' ')})</SelectItem>
    );
  }

  var monthSelectItems = [];
  for (var k = 0; k < monthOptions.length; k++) {
    monthSelectItems.push(
      <SelectItem key={monthOptions[k].value} value={monthOptions[k].value}>{monthOptions[k].label}</SelectItem>
    );
  }

  var slipItems = [];
  for (var m = 0; m < salarySlips.length; m++) {
    var slip = salarySlips[m];
    var monthDate = new Date(slip.month + '-01');
    var monthLabel = monthDate.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    var typeClass = slip.employee_type === 'fleet_manager' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300';
    
    slipItems.push(
      <div key={slip.id} className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white font-medium">{slip.employee_name}</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge className={typeClass}>{slip.employee_type.replace('_', ' ')}</Badge>
              <span className="text-sm text-gray-400">{monthLabel}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <p className="text-lg font-bold text-green-400">₹{slip.amount.toLocaleString()}</p>
          <a href={slip.file_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            <Download className="w-4 h-4 text-white" />
          </a>
          <Button variant="ghost" size="sm" onClick={function(id) { return function() { handleDelete(id); }; }(slip.id)} className="text-red-400">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-white/10">
        <CardContent className="p-6 text-center text-gray-400">Loading salary slips...</CardContent>
      </Card>
    );
  }

  return (
    <div>
      <Card className="bg-slate-800/50 border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-green-400" />
            Salary Slips (Fixed Employees)
          </CardTitle>
          <Button onClick={function() { setShowUploadModal(true); }} className="bg-green-600 hover:bg-green-700">
            <Upload className="w-4 h-4 mr-2" />Upload Salary Slip
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-gray-400 text-sm mb-4">Upload monthly salary slips for Fleet Managers and HR/Operational staff. Flex employees receive task-based payments instead.</p>
          <div className="space-y-3">
            {slipItems}
            {salarySlips.length === 0 && (
              <p className="text-center text-gray-400 py-8">No salary slips uploaded yet</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="bg-slate-800 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-400" />
              Upload Salary Slip
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Select Employee</label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger className="bg-slate-700/50 border-white/10 text-white">
                  <SelectValue placeholder="Choose employee" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  {employeeOptions}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Month</label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="bg-slate-700/50 border-white/10 text-white">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-white/10">
                  {monthSelectItems}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Net Salary Amount (₹)</label>
              <Input 
                type="number" 
                value={amount} 
                onChange={function(e) { setAmount(e.target.value); }} 
                placeholder="e.g., 25000" 
                className="bg-slate-700/50 border-white/10 text-white" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (Optional)</label>
              <Input 
                value={notes} 
                onChange={function(e) { setNotes(e.target.value); }} 
                placeholder="Any additional notes" 
                className="bg-slate-700/50 border-white/10 text-white" 
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Salary Slip (PDF/Image)</label>
              <label className="flex items-center justify-center p-4 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-green-500/50">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-400">{file ? file.name : 'Click to upload file'}</p>
                </div>
                <input 
                  type="file" 
                  accept=".pdf,.jpg,.jpeg,.png" 
                  className="hidden" 
                  onChange={function(e) { if (e.target.files[0]) setFile(e.target.files[0]); }} 
                />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleUpload} disabled={uploading} className="bg-green-600 hover:bg-green-700">
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

